class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );

    const overlay =
      this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this)
      );
    }

    this.setHeaderCartIconAccessibility();
  }


  /* ==========================================
     HEADER CART ICON
  ========================================== */

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.open(cartLink);
      }
    );

    cartLink.addEventListener(
      'keydown',
      (event) => {
        if (
          event.code.toUpperCase() === 'SPACE'
        ) {
          event.preventDefault();
          this.open(cartLink);
        }
      }
    );
  }


  /* ==========================================
     OPEN DRAWER
  ========================================== */

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote =
      this.querySelector(
        '[id^="Details-"] summary'
      );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }

    window.setTimeout(() => {
      this.classList.add(
        'animate',
        'active'
      );
    }, 10);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector(
                '.drawer__inner-empty'
              )
            : document.getElementById(
                'CartDrawer'
              );

        const focusElement =
          this.querySelector(
            '.drawer__inner'
          ) ||
          this.querySelector(
            '.drawer__close'
          );

        if (
          containerToTrapFocusOn &&
          focusElement
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },
      { once: true }
    );

    document.body.classList.add(
      'overflow-hidden'
    );

    this
      .querySelector('cart-drawer-items')
      ?.dispatchViewEvent();
  }


  /* ==========================================
     CLOSE DRAWER
  ========================================== */

  close() {
    this.classList.remove('active');

    if (this.activeElement) {
      removeTrapFocus(
        this.activeElement
      );
    }

    document.body.classList.remove(
      'overflow-hidden'
    );
  }


  /* ==========================================
     SUMMARY ACCESSIBILITY
  ========================================== */

  setSummaryAccessibility(
    cartDrawerNote
  ) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote.nextElementSibling
        ?.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote
          .nextElementSibling
          .id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget
            .closest('details')
            .hasAttribute('open')
        );
      }
    );

    cartDrawerNote.parentElement
      ?.addEventListener(
        'keyup',
        onKeyUpEscape
      );
  }


  /* ==========================================
     RENDER CART

     IMPORTANT:
     After Shopify adds the product,
     fetch the cart drawer AGAIN so we always
     render the newest cart state.

     This fixes collection Quick Add where
     the product was added but did not appear
     until the page was refreshed.
  ========================================== */

  async renderContents(parsedState) {
    this.productId =
      parsedState?.id || null;

    try {

      const cartUrl =
        window.routes?.cart_url ||
        '/cart';

      const separator =
        cartUrl.includes('?')
          ? '&'
          : '?';

      const response =
        await fetch(
          `${cartUrl}${separator}sections=cart-drawer,cart-icon-bubble`,
          {
            method: 'GET',
            headers: {
              'X-Requested-With':
                'XMLHttpRequest'
            }
          }
        );

      if (!response.ok) {
        throw new Error(
          'Unable to refresh cart drawer.'
        );
      }

      const sections =
        await response.json();


      /* CART DRAWER */

      if (
        sections['cart-drawer']
      ) {
        const html =
          new DOMParser()
            .parseFromString(
              sections['cart-drawer'],
              'text/html'
            );

        const freshDrawer =
          html.querySelector(
            '#CartDrawer'
          );

        const currentDrawer =
          document.querySelector(
            '#CartDrawer'
          );

        if (
          freshDrawer &&
          currentDrawer
        ) {
          currentDrawer.innerHTML =
            freshDrawer.innerHTML;
        }
      }


      /* CART ICON BUBBLE */

      if (
        sections['cart-icon-bubble']
      ) {
        const html =
          new DOMParser()
            .parseFromString(
              sections[
                'cart-icon-bubble'
              ],
              'text/html'
            );

        const freshBubble =
          html.querySelector(
            '#cart-icon-bubble'
          );

        const currentBubble =
          document.querySelector(
            '#cart-icon-bubble'
          );

        if (
          freshBubble &&
          currentBubble
        ) {
          currentBubble.innerHTML =
            freshBubble.innerHTML;
        }
      }


      /* REMOVE EMPTY STATE */

      this.classList.remove(
        'is-empty'
      );

      const drawerInner =
        this.querySelector(
          '.drawer__inner'
        );

      if (drawerInner) {
        drawerInner.classList.remove(
          'is-empty'
        );
      }


      /* RECONNECT OVERLAY */

      const overlay =
        this.querySelector(
          '#CartDrawer-Overlay'
        );

      if (overlay) {
        overlay.addEventListener(
          'click',
          this.close.bind(this)
        );
      }


      /* OPEN UPDATED DRAWER */

      this.open();

    }

    catch (error) {

      console.error(
        'Pure Craft cart drawer refresh error:',
        error
      );


      /*
        FALLBACK:
        If fresh section request ever fails,
        use Shopify's original returned sections.
      */

      if (
        parsedState &&
        parsedState.sections
      ) {

        this
          .getSectionsToRender()
          .forEach(
            (section) => {

              const sectionElement =
                section.selector
                  ? document.querySelector(
                      section.selector
                    )
                  : document.getElementById(
                      section.id
                    );

              const sectionHtml =
                parsedState.sections[
                  section.id
                ];

              if (
                !sectionElement ||
                !sectionHtml
              ) {
                return;
              }

              sectionElement.innerHTML =
                this.getSectionInnerHTML(
                  sectionHtml,
                  section.selector
                );
            }
          );

        const overlay =
          this.querySelector(
            '#CartDrawer-Overlay'
          );

        if (overlay) {
          overlay.addEventListener(
            'click',
            this.close.bind(this)
          );
        }

        this.open();
      }
    }
  }


  /* ==========================================
     SECTION HTML
  ========================================== */

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const documentHtml =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );

    const element =
      documentHtml.querySelector(
        selector
      );

    return element
      ? element.innerHTML
      : '';
  }


  /* ==========================================
     SECTIONS
  ========================================== */

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }


  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(
        selector
      );
  }


  setActiveElement(element) {
    this.activeElement =
      element;
  }
}


customElements.define(
  'cart-drawer',
  CartDrawer
);


/* ==========================================
   CART DRAWER ITEMS
========================================== */

class CartDrawerItems
  extends CartItems {

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}


customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);